import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { UnitsGridColumn } from 'src/app/shared/components/units-grid-input/units-grid-input.models';
import { DlsOverrideModel } from 'src/app/shared/models/dlsOverride.model';

export const DEFAULT_DLS_OVERRIDE_ROW: DlsOverrideModel = {
  measuredDepth: 0,
  dlsOverride: 0
};

export function createDlsOverrideColumns(): readonly UnitsGridColumn[] {
  return [
    {
      kind: 'number',
      field: 'measuredDepth',
      header: 'Bottom Measured Depth',
      unitKey: 'longLengths',
      minFractionDigits: 2,
      maxFractionDigits: 6,
      formatFractionDigits: 3
    },
    {
      kind: 'number',
      field: 'dlsOverride',
      header: 'Additional Dogleg',
      unitKey: 'doglegSeverity',
      minFractionDigits: 2,
      maxFractionDigits: 6,
      formatFractionDigits: 4
    }
  ];
}

export function createDlsOverrideRow(data?: Partial<DlsOverrideModel>): FormGroup {
  return new FormGroup({
    measuredDepth: new FormControl<number | null>(data?.measuredDepth ?? null, [
      Validators.required,
      Validators.min(0)
    ]),
    dlsOverride: new FormControl<number | null>(data?.dlsOverride ?? null, [
      Validators.required,
      Validators.min(0)
    ])
  });
}

export function isDlsOverrideRowEmpty(row: FormGroup): boolean {
  const md = row.get('measuredDepth')?.value;
  const dls = row.get('dlsOverride')?.value;

  const isMdEmpty = md === null || md === undefined || md === '' || Number.isNaN(Number(md));
  const isDlsEmpty = dls === null || dls === undefined || dls === '' || Number.isNaN(Number(dls));

  return isMdEmpty && isDlsEmpty;
}

export function ensureTrailingInputRow(formArray: FormArray<FormGroup>): void {
  if (formArray.length === 0) {
    formArray.push(createDlsOverrideRow());
    return;
  }

  while (
    formArray.length > 1 &&
    isDlsOverrideRowEmpty(formArray.at(formArray.length - 1)) &&
    isDlsOverrideRowEmpty(formArray.at(formArray.length - 2))
  ) {
    formArray.removeAt(formArray.length - 1);
  }

  const lastRow = formArray.at(formArray.length - 1);
  if (!isDlsOverrideRowEmpty(lastRow)) {
    formArray.push(createDlsOverrideRow());
  }
}

export function populateDlsOverrideFormArray(
  formArray: FormArray<FormGroup>,
  dlsOverrides?: readonly DlsOverrideModel[]
): void {
  formArray.clear();
  const rows = dlsOverrides && dlsOverrides.length > 0 ? dlsOverrides : [];

  rows.forEach((item) => {
    formArray.push(createDlsOverrideRow(item));
  });

  ensureTrailingInputRow(formArray);
}

export function extractDlsOverrideRows(
  formArray: FormArray<FormGroup>,
  defaultTotalDepth = 0
): DlsOverrideModel[] {
  const validRows: DlsOverrideModel[] = formArray.controls
    .filter((control) => !isDlsOverrideRowEmpty(control as FormGroup))
    .map((control) => ({
      measuredDepth: Number(control.get('measuredDepth')?.value ?? 0),
      dlsOverride: Number(control.get('dlsOverride')?.value ?? 0)
    }));

  const uniqueRows = validRows.filter(
    (row, index, self) =>
      index === self.findIndex((r) => r.measuredDepth === row.measuredDepth && r.dlsOverride === row.dlsOverride)
  );

  return uniqueRows.length === 0
    ? [{ measuredDepth: defaultTotalDepth, dlsOverride: 0 }]
    : uniqueRows;
}
